import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, X, Save, ImageIcon, CalendarClock,
  Radio, Users, MessageCircle, GripVertical, ChevronLeft, Mic, Check, Volume2, Languages,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const VOICE_META = {
  onyx: { label: "Onyx", desc: "Homme grave et autoritaire" },
  echo: { label: "Echo", desc: "Homme mûr, posé et chaleureux" },
  ash: { label: "Ash", desc: "Homme clair, assuré et articulé" },
};

function VoiceSelector() {
  const [voice, setVoice] = useState(null);
  const [saving, setSaving] = useState("");
  const [previewing, setPreviewing] = useState("");
  const audioRef = useState(() => (typeof Audio !== "undefined" ? new Audio() : null))[0];
  useEffect(() => {
    api.get("/admin/voice").then(({ data }) => setVoice(data.voice)).catch(() => {});
    return () => { if (audioRef) { audioRef.pause(); } };
  }, []);
  const pick = async (v) => {
    setSaving(v);
    try {
      await api.put("/admin/voice", { voice: v });
      setVoice(v);
      toast.success(`Voix des narrations : ${VOICE_META[v]?.label || v}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally { setSaving(""); }
  };
  const preview = async (v) => {
    if (!audioRef) return;
    setPreviewing(v);
    try {
      audioRef.pause();
      const { data } = await api.get(`/admin/voice/preview?voice=${v}`);
      audioRef.src = `data:audio/mp3;base64,${data.audio}`;
      audioRef.onended = () => setPreviewing("");
      await audioRef.play();
    } catch (e) {
      setPreviewing("");
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Aperçu indisponible");
    }
  };
  return (
    <div className="glass rounded-2xl p-5 frame-pulse" data-testid="voice-selector">
      <div className="flex items-center gap-2 mb-1">
        <Mic size={17} className="text-primary" />
        <h4 className="font-display text-lg text-foreground/90">Voix des narrations</h4>
      </div>
      <p className="text-xs text-foreground/45 mb-4">Voix unique (HD) pour la conférence, la lettre des fondateurs, La Dérive et la voix de L'Élan. Écoute un extrait avant de choisir.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {["onyx", "echo", "ash"].map((v) => {
          const on = voice === v;
          return (
            <div key={v}
              className={`relative rounded-xl border px-4 py-3 transition-colors ${on ? "border-primary/50 bg-primary/[0.08]" : "border-white/10 bg-white/[0.02]"}`}
              data-testid={`voice-option-${v}`}>
              <div className="flex items-center justify-between">
                <span className={`font-medium ${on ? "text-primary" : "text-foreground/80"}`}>{VOICE_META[v].label}</span>
                {on && <Check size={15} className="text-primary" />}
              </div>
              <span className="text-xs text-foreground/45 block mt-0.5 mb-3">{VOICE_META[v].desc}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => preview(v)} disabled={previewing === v}
                  data-testid={`voice-preview-${v}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-50">
                  {previewing === v ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />} Écouter
                </button>
                {!on && (
                  <button onClick={() => pick(v)} disabled={!!saving || voice === null}
                    data-testid={`voice-choose-${v}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary px-3 py-1.5 text-xs font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
                    {saving === v ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Choisir
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const STATUS_META = {
  live: { label: "En direct", cls: "bg-red-500/15 text-red-400 border-red-500/40" },
  published: { label: "En cours", cls: "bg-primary/15 text-primary border-primary/40" },
  upcoming: { label: "Prochainement", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  ended: { label: "Terminée", cls: "bg-white/10 text-foreground/50 border-white/15" },
};

const EMPTY_CHAPTER = () => ({ num: "", title: "", body: [""], highlights: [], table: [], quote: "" });
const EMPTY_CONF = () => ({
  id: "", month: "", eyebrow: "La Conférence du mois", title: "", subtitle: "", teaser: "",
  cover_url: "", publish_at: "", live_start: "", live_end: "", intro: "",
  chapters: [EMPTY_CHAPTER()], closing_question: "",
});

// ISO (UTC) <-> datetime-local (treated as UTC)
const isoToLocal = (iso) => (iso ? iso.slice(0, 16) : "");
const localToIso = (v) => (v ? `${v}:00+00:00` : "");

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-foreground/50 mb-1.5 block">{label}</span>
      {children}
      {hint && <span className="text-xs text-foreground/35 mt-1 block">{hint}</span>}
    </label>
  );
}

const inputCls = "w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 outline-none text-foreground placeholder:text-foreground/30 focus:border-primary/50 transition-colors text-sm";

function StringList({ label, items, onChange, placeholder }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-foreground/50">{label}</span>
        <button type="button" onClick={() => onChange([...items, ""])} className="text-primary hover:text-primary/80 inline-flex items-center gap-1 text-xs" data-testid="add-list-item">
          <Plus size={13} /> Ajouter
        </button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-xs text-foreground/30 italic">Aucun élément.</p>}
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <textarea rows={2} value={it} placeholder={placeholder}
              onChange={(e) => { const n = [...items]; n[i] = e.target.value; onChange(n); }}
              className={`${inputCls} resize-y`} />
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-foreground/40 hover:text-red-400 mt-2 flex-shrink-0">
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChapterEditor({ chapter, index, onChange, onRemove }) {
  const set = (patch) => onChange({ ...chapter, ...patch });
  return (
    <div className="glass rounded-2xl p-5 border border-white/10" data-testid={`chapter-editor-${index}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-foreground/60">
          <GripVertical size={16} /> <span className="font-display text-lg">Chapitre {index + 1}</span>
        </div>
        <button type="button" onClick={onRemove} className="text-foreground/40 hover:text-red-400 inline-flex items-center gap-1 text-sm" data-testid={`remove-chapter-${index}`}>
          <Trash2 size={15} /> Supprimer
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-3 mb-4">
        <Field label="Numéro"><input value={chapter.num} onChange={(e) => set({ num: e.target.value })} placeholder="I" className={inputCls} data-testid={`chapter-num-${index}`} /></Field>
        <Field label="Titre du chapitre"><input value={chapter.title} onChange={(e) => set({ title: e.target.value })} placeholder="Titre" className={inputCls} data-testid={`chapter-title-${index}`} /></Field>
      </div>
      <div className="space-y-4">
        <StringList label="Paragraphes" items={chapter.body} onChange={(body) => set({ body })} placeholder="Un paragraphe de texte…" />
        <StringList label="Points-clés (liste à puces)" items={chapter.highlights} onChange={(highlights) => set({ highlights })} placeholder="Un point-clé…" />
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs uppercase tracking-wider text-foreground/50">Tableau (2 colonnes)</span>
            <button type="button" onClick={() => set({ table: [...chapter.table, { label: "", desc: "" }] })} className="text-primary hover:text-primary/80 inline-flex items-center gap-1 text-xs">
              <Plus size={13} /> Ligne
            </button>
          </div>
          {chapter.table.length === 0 && <p className="text-xs text-foreground/30 italic">Aucune ligne.</p>}
          <div className="space-y-2">
            {chapter.table.map((row, k) => (
              <div key={k} className="flex items-start gap-2">
                <input value={row.label} placeholder="Intitulé" onChange={(e) => { const t = [...chapter.table]; t[k] = { ...t[k], label: e.target.value }; set({ table: t }); }} className={`${inputCls} sm:w-1/3`} />
                <textarea rows={2} value={row.desc} placeholder="Description" onChange={(e) => { const t = [...chapter.table]; t[k] = { ...t[k], desc: e.target.value }; set({ table: t }); }} className={`${inputCls} resize-y flex-1`} />
                <button type="button" onClick={() => set({ table: chapter.table.filter((_, j) => j !== k) })} className="text-foreground/40 hover:text-red-400 mt-2 flex-shrink-0"><X size={16} /></button>
              </div>
            ))}
          </div>
        </div>
        <Field label="Citation (optionnel)"><textarea rows={2} value={chapter.quote} onChange={(e) => set({ quote: e.target.value })} placeholder="Une citation forte…" className={`${inputCls} resize-y`} data-testid={`chapter-quote-${index}`} /></Field>
      </div>
    </div>
  );
}

function ConferenceForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState(() => {
    const base = { ...EMPTY_CONF(), ...(initial || {}) };
    base.chapters = (initial?.chapters?.length ? initial.chapters : [EMPTY_CHAPTER()]).map((c) => ({
      num: c.num || "", title: c.title || "", body: c.body?.length ? c.body : [""],
      highlights: c.highlights || [], table: c.table || [], quote: c.quote || "",
    }));
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [coverGen, setCoverGen] = useState(false);
  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

  const genCover = async () => {
    setCoverGen(true);
    try {
      const { data } = await api.post("/admin/conferences/cover", { title: f.title, subtitle: f.subtitle });
      set({ cover_url: `${API}/api/promo/image/${data.image_id}` });
      toast.success("Couverture générée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de génération");
    } finally { setCoverGen(false); }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Le titre est obligatoire."); return; }
    setSaving(true);
    const payload = {
      id: f.id || "", month: f.month, eyebrow: f.eyebrow, title: f.title, subtitle: f.subtitle,
      teaser: f.teaser, cover_url: f.cover_url, publish_at: f.publish_at, live_start: f.live_start,
      live_end: f.live_end, intro: f.intro, closing_question: f.closing_question,
      chapters: f.chapters,
    };
    try {
      const { data: res } = isEdit
        ? await api.put(`/admin/conferences/${f.id}`, payload)
        : await api.post("/admin/conferences", payload);
      toast.success(isEdit ? "Conférence mise à jour" : "Conférence créée");
      if (res?.translating) toast.info("Traduction automatique EN/ES/NL lancée en arrière-plan.");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="conference-form">
      <button onClick={onCancel} className="inline-flex items-center gap-2 text-foreground/50 hover:text-primary text-sm" data-testid="conf-form-back">
        <ChevronLeft size={16} /> Retour à la liste
      </button>

      {/* Base */}
      <div className="glass rounded-2xl p-6 frame-pulse space-y-4">
        <h3 className="font-display text-xl text-gradient-orange">{isEdit ? "Modifier la conférence" : "Nouvelle conférence"}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Titre"><input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="L'aristocratie du temps" className={inputCls} data-testid="conf-title" /></Field>
          <Field label="Mois affiché"><input value={f.month} onChange={(e) => set({ month: e.target.value })} placeholder="Juillet 2026" className={inputCls} data-testid="conf-month" /></Field>
        </div>
        <Field label="Sous-titre"><input value={f.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className={inputCls} data-testid="conf-subtitle" /></Field>
        <Field label="Teaser (aperçu « Prochainement »)"><textarea rows={2} value={f.teaser} onChange={(e) => set({ teaser: e.target.value })} className={`${inputCls} resize-y`} data-testid="conf-teaser" /></Field>
        <Field label="Image de couverture (URL)" hint="Colle une URL d'image ou génère une couverture avec l'IA.">
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={f.cover_url} onChange={(e) => set({ cover_url: e.target.value })} placeholder="https://…" className={`${inputCls} flex-1`} data-testid="conf-cover-url" />
            <button type="button" onClick={genCover} disabled={coverGen} className="btn-frost-orange inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50 whitespace-nowrap" data-testid="conf-cover-gen">
              {coverGen ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />} Générer (IA)
            </button>
          </div>
        </Field>
        {f.cover_url && <img src={f.cover_url} alt="cover" className="w-full max-h-56 object-cover rounded-xl border border-white/10" data-testid="conf-cover-preview" />}
      </div>

      {/* Planning */}
      <div className="glass rounded-2xl p-6 frame-pulse space-y-4">
        <h4 className="font-display text-lg text-foreground/85 flex items-center gap-2"><CalendarClock size={18} className="text-primary" /> Planification (UTC)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Publication le" hint="Devient « en cours » à cette date.">
            <input type="datetime-local" value={isoToLocal(f.publish_at)} onChange={(e) => set({ publish_at: localToIso(e.target.value) })} className={inputCls} data-testid="conf-publish-at" />
          </Field>
          <Field label="Début du direct">
            <input type="datetime-local" value={isoToLocal(f.live_start)} onChange={(e) => set({ live_start: localToIso(e.target.value) })} className={inputCls} data-testid="conf-live-start" />
          </Field>
          <Field label="Fin du direct">
            <input type="datetime-local" value={isoToLocal(f.live_end)} onChange={(e) => set({ live_end: localToIso(e.target.value) })} className={inputCls} data-testid="conf-live-end" />
          </Field>
        </div>
      </div>

      {/* Content */}
      <div className="glass rounded-2xl p-6 frame-pulse space-y-4">
        <h4 className="font-display text-lg text-foreground/85">Contenu</h4>
        <Field label="Introduction"><textarea rows={4} value={f.intro} onChange={(e) => set({ intro: e.target.value })} className={`${inputCls} resize-y`} data-testid="conf-intro" /></Field>
        <Field label="Question de clôture"><textarea rows={2} value={f.closing_question} onChange={(e) => set({ closing_question: e.target.value })} className={`${inputCls} resize-y`} data-testid="conf-closing" /></Field>
      </div>

      {/* Chapters */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-display text-lg text-foreground/85">Chapitres ({f.chapters.length})</h4>
          <button type="button" onClick={() => set({ chapters: [...f.chapters, EMPTY_CHAPTER()] })} className="btn-frost-orange inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm" data-testid="add-chapter">
            <Plus size={15} /> Ajouter un chapitre
          </button>
        </div>
        {f.chapters.map((ch, i) => (
          <ChapterEditor key={i} chapter={ch} index={i}
            onChange={(nc) => { const arr = [...f.chapters]; arr[i] = nc; set({ chapters: arr }); }}
            onRemove={() => set({ chapters: f.chapters.filter((_, j) => j !== i) })} />
        ))}
      </div>

      <div className="flex items-center gap-3 sticky bottom-4 z-10">
        <button onClick={save} disabled={saving} className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold text-sm disabled:opacity-50" data-testid="conf-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {isEdit ? "Enregistrer" : "Créer la conférence"}
        </button>
        <button onClick={onCancel} className="px-6 py-3 rounded-full border border-white/15 text-foreground/60 hover:text-foreground/90 text-sm" data-testid="conf-cancel-btn">Annuler</button>
      </div>
    </div>
  );
}

export default function ConferencesSection() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // conf object or {} for new
  const [deleting, setDeleting] = useState("");
  const [translating, setTranslating] = useState("");

  const load = () => api.get("/admin/conferences").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const onSaved = () => { setEditing(null); load(); };

  const remove = async (cid) => {
    if (!window.confirm("Supprimer cette conférence ? Les inscriptions et interventions seront conservées.")) return;
    setDeleting(cid);
    try { await api.delete(`/admin/conferences/${cid}`); toast.success("Conférence supprimée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    finally { setDeleting(""); }
  };

  const translate = async (cid) => {
    setTranslating(cid);
    try {
      const { data: res } = await api.post(`/admin/conferences/${cid}/translate`);
      const ok = (res.translated || []).length;
      if (ok) toast.success(`Traduite en ${res.translated.join(", ").toUpperCase()}`);
      else toast.info("Déjà traduite en EN, ES et NL.");
      if ((res.failed || []).length) toast.error(`Échec : ${res.failed.join(", ").toUpperCase()}. Réessaie.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "La traduction a échoué.");
    } finally { setTranslating(""); }
  };

  const translateAll = async () => {
    if (!window.confirm("Lancer la traduction IA (EN/ES/NL) de toutes les conférences non encore traduites ? Cela s'exécute en arrière-plan (quelques minutes).")) return;
    try {
      await api.post("/admin/conferences/translate-all");
      toast.success("Traduction lancée en arrière-plan. Actualise dans quelques minutes.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  if (editing !== null) {
    return <ConferenceForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={onSaved} />;
  }
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary/60" size={28} /></div>;

  return (
    <div className="space-y-5" data-testid="conferences-section">
      <VoiceSelector />
      <div className="flex items-center justify-between gap-3">
        <p className="text-foreground/60 text-sm">Gère le calendrier des conférences directement ici — aucune modification de code.</p>
        <div className="flex items-center gap-2">
          <button onClick={translateAll} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-primary/30 text-primary/85 hover:text-primary hover:border-primary/60 hover:bg-primary/[0.06] text-sm transition-all whitespace-nowrap" data-testid="conf-translate-all-btn">
            <Languages size={16} /> Tout traduire
          </button>
          <button onClick={() => setEditing({})} className="btn-frost-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap" data-testid="conf-new-btn">
            <Plus size={16} /> Nouvelle
          </button>
        </div>
      </div>

      {data.conferences.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-foreground/40 italic">Aucune conférence. Crée la première.</div>
      ) : data.conferences.map((c) => {
        const meta = STATUS_META[c.status] || STATUS_META.published;
        return (
          <motion.div key={c.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl overflow-hidden frame-pulse flex flex-col sm:flex-row" data-testid={`conf-card-${c.id}`}>
            {c.cover_url && <img src={c.cover_url} alt={c.title} className="w-full sm:w-48 h-32 sm:h-auto object-cover flex-shrink-0" />}
            <div className="p-5 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-wider border ${meta.cls}`} data-testid={`conf-status-${c.id}`}>
                  {c.status === "live" && <Radio size={11} />} {meta.label}
                </span>
                {c.is_current && <span className="text-[11px] text-primary/70 uppercase tracking-wider">· Affichée sur la home</span>}
                <span className="text-xs text-foreground/40">{c.month}</span>
                {(() => {
                  const langs = ["en", "es", "nl"].filter((l) => (c.i18n || {})[l]);
                  const full = langs.length === 3;
                  return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${full ? "border-emerald-400/30 text-emerald-300/80" : "border-white/10 text-foreground/40"}`} data-testid={`conf-langs-${c.id}`}>
                      <Languages size={10} /> {full ? "FR·EN·ES·NL" : langs.length ? `FR + ${langs.join(",").toUpperCase()}` : "FR uniquement"}
                    </span>
                  );
                })()}
              </div>
              <h4 className="font-display text-xl text-foreground/90 truncate">{c.title}</h4>
              <p className="text-sm text-foreground/50 truncate">{c.subtitle}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-foreground/45">
                <span className="inline-flex items-center gap-1.5"><Users size={13} className="text-primary/60" /> {c.registrations} inscrit(s)</span>
                <span className="inline-flex items-center gap-1.5"><MessageCircle size={13} className="text-primary/60" /> {c.interventions_count} message(s)</span>
                <span className="inline-flex items-center gap-1.5"><CalendarClock size={13} className="text-primary/60" /> {c.chapters?.length || 0} chapitre(s)</span>
              </div>
            </div>
            <div className="flex sm:flex-col items-center justify-end gap-2 p-4 border-t sm:border-t-0 sm:border-l border-white/5">
              <button onClick={() => setEditing(c)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary/15 text-primary text-sm hover:bg-primary/25 transition-colors" data-testid={`conf-edit-${c.id}`}>
                <Pencil size={14} /> Modifier
              </button>
              <button onClick={() => translate(c.id)} disabled={translating === c.id} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-primary/25 text-primary/80 hover:text-primary hover:border-primary/50 text-sm transition-colors disabled:opacity-50" data-testid={`conf-translate-${c.id}`}>
                {translating === c.id ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />} Traduire
              </button>
              <button onClick={() => remove(c.id)} disabled={deleting === c.id} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/10 text-foreground/50 hover:text-red-400 hover:border-red-400/40 text-sm transition-colors disabled:opacity-50" data-testid={`conf-delete-${c.id}`}>
                {deleting === c.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Supprimer
              </button>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
